import { removeFontFaceRules, setupHtmlImportSanitization } from './htmlImport.utils';

export interface FontVariant {
  source?: string;
}

export interface FontConfig {
  family: string;
  variants: {
    regular: FontVariant;
    [key: string]: FontVariant;
  };
}

export interface ProjectFontConfig {
  [fontFamily: string]: {
    variants: {
      regular: FontVariant;
      [key: string]: FontVariant;
    };
  };
}

const FONT_DEFINITIONS: FontConfig[] = [
  {
    family: 'Bebas Neue',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/bebasneue/v16/JTUSjIg69CK48gW7PXooxW4.ttf',
      },
    },
  },
  {
    family: 'Bodoni Moda',
    variants: {
      regular: {
        source:
          'https://fonts.gstatic.com/s/bodonimoda/v28/aFT67PxzY382XsXX63LUYL6GYFcan6NJrKp-VPjfJMShrpsGFUt8oU7awIA.ttf',
      },
      '700': {
        source:
          'https://fonts.gstatic.com/s/bodonimoda/v28/aFT67PxzY382XsXX63LUYL6GYFcan6NJrKp-VPjfJMShrpsGFUt8oandwIA.ttf',
      },
    },
  },
  {
    family: 'Climate Crisis',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/climatecrisis/v14/wEOpEB3AntNeKCPBVW9XOKlmp3AUgWFN1DvIvcM0gFp6jQ.ttf',
      },
    },
  },
  {
    family: 'Lato',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/lato/v25/S6uyw4BMUTPHvxk.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/lato/v25/S6u9w4BMUTPHh6UVew8.ttf',
      },
    },
  },
  {
    family: 'Noto Sans',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/notosans/v42/o-0mIpQlx3QUlC5A4PNB6Ryti20_6n1iPHjcz6L1SoM-jCpoiyD9A99d.ttf',
      },
      '600': {
        source: 'https://fonts.gstatic.com/s/notosans/v42/o-0mIpQlx3QUlC5A4PNB6Ryti20_6n1iPHjcz6L1SoM-jCpoiyAjBN9d.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/notosans/v42/o-0mIpQlx3QUlC5A4PNB6Ryti20_6n1iPHjcz6L1SoM-jCpoiyAaBN9d.ttf',
      },
    },
  },
  {
    family: 'Playfair Display',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/playfairdisplay/v40/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvUDQ.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/playfairdisplay/v40/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKeiukDQ.ttf',
      },
    },
  },
  {
    family: 'Poiret One',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/poiretone/v18/UqyVK80NJXN4zfRgbdfbk5k.ttf',
      },
    },
  },
  {
    family: 'Roboto',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWubEbWmT.ttf',
      },
      '500': {
        source: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWub2bWmT.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuYjammT.ttf',
      },
    },
  },
  {
    family: 'Rubik',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/rubik/v31/iJWZBXyIfDnIV5PNhY1KTN7Z-Yh-B4i1UA.ttf',
      },
      '500': {
        source: 'https://fonts.gstatic.com/s/rubik/v31/iJWZBXyIfDnIV5PNhY1KTN7Z-Yh-NYi1UA.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/rubik/v31/iJWZBXyIfDnIV5PNhY1KTN7Z-Yh-4I-1UA.ttf',
      },
    },
  },
  {
    family: 'Source Sans Pro',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/sourcesanspro/v23/6xK3dSBYKcSV-LCoeQqfX1RYOo3aPw.ttf',
      },
      '600': {
        source: 'https://fonts.gstatic.com/s/sourcesanspro/v23/6xKydSBYKcSV-LCoeQqfX1RYOo3i54rAkA.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/sourcesanspro/v23/6xKydSBYKcSV-LCoeQqfX1RYOo3ig4vAkA.ttf',
      },
    },
  },
  {
    family: 'Teachers',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/teachers/v6/H4c5BXKVncXVmUGsgTwx4E9oHx2xfjBr3powYw.ttf',
      },
      '700': {
        source: 'https://fonts.gstatic.com/s/teachers/v6/H4c5BXKVncXVmUGsgTwx4E9oHx2xfjBrOZ0wYw.ttf',
      },
    },
  },
  {
    family: 'Young Serif',
    variants: {
      regular: {
        source: 'https://fonts.gstatic.com/s/youngserif/v2/3qTpojO2nS2VtkB3KtkQZ2t6.ttf',
      },
    },
  },
];

/**
 * Normalize font family name for sorting: remove quotes, handle fallbacks, trim whitespace
 * @param family - Font family name to normalize
 * @returns Normalized font family name (lowercase, no quotes, primary name only)
 */
const normalizeFamilyForSort = (family: string): string => {
  if (!family) return '';
  let normalized = family.replace(/['"]/g, '');
  const firstPart = normalized.split(',')[0];
  return firstPart.trim().toLowerCase();
};

export const getFontsDefault = (baseDefault: any[]): FontConfig[] => {
  const normalizedBaseDefault = (baseDefault || [])
    .map((font: any) => {
      if (!font) return null;

      if (font.family) {
        return font;
      }

      if (typeof font === 'string') {
        return {
          family: font,
          variants: { regular: {} },
        };
      }

      // If it has 'id' or 'label', use that as the family
      const familyName = font.id || font.label || font.name;
      if (familyName) {
        return {
          ...font,
          family: familyName,
          variants: font.variants || { regular: {} },
        };
      }

      // If it's an object with other properties, try to preserve it but add family
      if (typeof font === 'object') {
        // Try to find any string property that might be the font name
        for (const key in font) {
          if (typeof font[key] === 'string' && key.toLowerCase().includes('name')) {
            return {
              ...font,
              family: font[key],
              variants: font.variants || { regular: {} },
            };
          }
        }
      }

      return null;
    })
    .filter((font: any) => font !== null && font.family);

  // Combine all fonts and sort them
  const allFonts = [...FONT_DEFINITIONS, ...normalizedBaseDefault].filter(font => font && font.family);
  const allFontsSorted = allFonts.sort((a, b) => {
    const familyA = normalizeFamilyForSort(a.family || '');
    const familyB = normalizeFamilyForSort(b.family || '');
    const comparison = familyA.localeCompare(familyB, undefined, { sensitivity: 'base' });
    return comparison;
  });

  return allFontsSorted;
};

/**
 * Get font configuration object for project.default.custom.globalPageSettings.fonts
 * Sorts fonts alphabetically before creating the config object
 * @returns Font configuration object keyed by font family name
 */
export const getProjectFontsConfig = (): ProjectFontConfig => {
  const config: ProjectFontConfig = {};

  // Sort fonts alphabetically before adding to config
  const sortedFonts = [...FONT_DEFINITIONS].sort((a, b) => {
    const familyA = normalizeFamilyForSort(a.family || '');
    const familyB = normalizeFamilyForSort(b.family || '');
    return familyA.localeCompare(familyB, undefined, { sensitivity: 'base' });
  });

  sortedFonts.forEach(font => {
    config[font.family] = {
      variants: font.variants,
    };
  });
  return config;
};

// Used in the generated HTML document
export const getFontsCssRules = (): string => {
  return FONT_DEFINITIONS.map(font => {
    return `@font-face {
      font-family: '${font.family}';
      src: url('${font.variants.regular.source}') format('truetype');
    }`;
  }).join('\n');
};

export const getTinyMceFontFormats = (includeDefaults: boolean = true): string => {
  const customFonts = FONT_DEFINITIONS.map(font => {
    const fontFamily = font.family;
    const cssValue = fontFamily.includes(' ') ? `'${fontFamily}'` : fontFamily;
    return `${fontFamily}=${cssValue},sans-serif`;
  });

  const defaultFonts = includeDefaults
    ? [
        'Andale Mono=andale mono,monospace',
        'Arial=arial,helvetica,sans-serif',
        'Arial Black=arial black,sans-serif',
        'Book Antiqua=book antiqua,palatino,sans-serif',
        'Comic Sans MS=comic sans ms,sans-serif',
        'Courier New=courier new,courier,monospace',
        'Georgia=georgia,palatino,sans-serif',
        'Helvetica=helvetica,sans-serif',
        'Impact=impact,sans-serif',
        'Symbol=symbol,sans-serif',
        'Tahoma=tahoma,arial,helvetica,sans-serif',
        'Terminal=terminal,monaco,monospace',
        'Times New Roman=times new roman,times,sans-serif',
        'Trebuchet MS=trebuchet ms,geneva,sans-serif',
        'Verdana=verdana,geneva,sans-serif',
      ]
    : [];

  const allFonts = [...defaultFonts, ...customFonts];
  const sortedFonts = allFonts.sort((a, b) => {
    const nameA = a.split('=')[0].toLowerCase();
    const nameB = b.split('=')[0].toLowerCase();
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  });

  return sortedFonts.join(';');
};

/**
 * Sets up interceptors and event listeners to prevent duplicate @font-face rules
 * in the GrapeJS editor. This handles cases where HTML is imported and contains @font-face rules.
 * @param editorInstance - The GrapeJS editor instance
 */
export const setupFontFaceInterceptors = (editorInstance: any): void => {
  // Intercept CSS additions to remove @font-face rules and prevent duplicates
  // This handles cases where HTML is imported and contains @font-face rules
  // Intercept multiple CSS methods that GrapeJS might use

  // Intercept addStyle method
  const originalAddStyle = editorInstance.addStyle;
  if (originalAddStyle) {
    editorInstance.addStyle = function (css: string, options?: any) {
      // Remove @font-face rules before adding CSS to prevent duplicates
      const filteredCss = removeFontFaceRules(css);
      return originalAddStyle.call(this, filteredCss, options);
    };
  }

  // Intercept setStyle method (used when importing HTML)
  const originalSetStyle = editorInstance.setStyle;
  if (originalSetStyle) {
    editorInstance.setStyle = function (css: string) {
      // Remove @font-face rules before setting CSS to prevent duplicates
      const filteredCss = removeFontFaceRules(css);
      return originalSetStyle.call(this, filteredCss);
    };
  }

  // Intercept CssComposer.add method (used internally by GrapeJS)
  if (editorInstance.CssComposer) {
    const originalCssAdd = editorInstance.CssComposer.add;
    if (originalCssAdd) {
      editorInstance.CssComposer.add = function (css: string, options?: any) {
        // Remove @font-face rules before adding CSS to prevent duplicates
        const filteredCss = removeFontFaceRules(css);
        return originalCssAdd.call(this, filteredCss, options);
      };
    }
  }

  // Set up HTML import sanitization (strips <head> content and filters @font-face rules)
  setupHtmlImportSanitization(editorInstance);

  // Listen to style:add event as a fallback to catch any CSS additions we might have missed
  editorInstance.on('style:add', (style: any) => {
    if (style && style.get && typeof style.get === 'function') {
      const cssText = style.get('cssText') || style.get('style');
      if (cssText && typeof cssText === 'string' && cssText.includes('@font-face')) {
        const filteredCss = removeFontFaceRules(cssText);
        if (filteredCss !== cssText) {
          style.set('cssText', filteredCss);
          style.set('style', filteredCss);
        }
      }
    }
  });

  // Also intercept CSS update events
  editorInstance.on('style:update', (style: any) => {
    if (style && style.get && typeof style.get === 'function') {
      const cssText = style.get('cssText') || style.get('style');
      if (cssText && typeof cssText === 'string' && cssText.includes('@font-face')) {
        const filteredCss = removeFontFaceRules(cssText);
        if (filteredCss !== cssText) {
          style.set('cssText', filteredCss);
          style.set('style', filteredCss);
        }
      }
    }
  });

  // Intercept component:add to filter @font-face from style tags in added components
  editorInstance.on('component:add', (component: any) => {
    if (component && component.find) {
      try {
        const styleComponents = component.find('style');
        if (styleComponents && styleComponents.length > 0) {
          styleComponents.forEach((styleComp: any) => {
            const content = styleComp.getContent();
            if (content && typeof content === 'string' && content.includes('@font-face')) {
              const filteredContent = removeFontFaceRules(content);
              if (filteredContent !== content) {
                styleComp.setContent(filteredContent);
              }
            }
          });
        }
      } catch (e) {
        // Silently ignore errors
      }
    }
  });

  // Clean CSS after canvas frame loads (when imported HTML is processed)
  editorInstance.on('canvas:frame:load', () => {
    try {
      const allCss = editorInstance.getCss();
      if (allCss && allCss.includes('@font-face')) {
        const filteredCss = removeFontFaceRules(allCss);
        if (filteredCss !== allCss) {
          editorInstance.setStyle(filteredCss);
        }
      }
    } catch (e) {
      // Silently ignore errors
    }
  });
};
